import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useCountryConfiguration } from 'src/hooks/useCountryConfiguration';
import TIMEZONE_TO_ISO from 'src/config/timezoneToCountry.json';

export type Region = 'India' | 'Global';

const STORAGE_KEY = 'symbian_selected_region';

function detectIndiaFromTimezone(): boolean {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz === 'Asia/Kolkata' || tz === 'Asia/Calcutta';
  } catch {
    return false;
  }
}

/** ISO code of the country the browser's timezone belongs to (Europe/Sofia → BG), or null. */
export function timezoneCountryIso(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return (TIMEZONE_TO_ISO as Record<string, string>)[tz] ?? null;
  } catch {
    return null;
  }
}

/** The browser's timezone maps to a known country other than India (e.g. Europe/Sofia → BG). */
function detectOtherCountryFromTimezone(): boolean {
  const iso = timezoneCountryIso();
  return !!iso && iso !== 'IN';
}

function getInitialRegion(): Region {
  // 1. Respect a persisted user choice
  const stored = localStorage.getItem(STORAGE_KEY) as Region | null;
  if (stored === 'India' || stored === 'Global') return stored;
  // 2. Auto-detect India from timezone
  if (detectIndiaFromTimezone()) return 'India';
  // 3. A recognised non-India timezone is a patient abroad: without this a patient in
  //    Sofia was treated as Indian (+91 phone codes, DPDP consent wording).
  if (detectOtherCountryFromTimezone()) return 'Global';
  // 4. Hard default → India (per product requirement)
  return 'India';
}

interface RegionContextType {
  region: Region;
  setRegion: (r: Region) => void;
  isIndia: boolean;
  isRegionLocked: boolean;
}

const RegionContext = createContext<RegionContextType | undefined>(undefined);

export const RegionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [region, setRegionState] = useState<Region>(getInitialRegion);

  const { isoCode, detectionSource } = useCountryConfiguration();
  const isRegionLocked = isoCode === 'IN' && detectionSource === 'timezone';
  const effectiveRegion: Region = isRegionLocked ? 'India' : region;

  const setRegion = (r: Region) => {
    if (isRegionLocked) return;
    localStorage.setItem(STORAGE_KEY, r);
    setRegionState(r);
  };

  // Sync across tabs
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && (e.newValue === 'India' || e.newValue === 'Global')) {
        setRegionState(e.newValue as Region);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  return (
    <RegionContext.Provider
      value={{
        region: effectiveRegion,
        setRegion,
        isIndia: effectiveRegion === 'India',
        isRegionLocked,
      }}
    >
      {children}
    </RegionContext.Provider>
  );
};

export const useRegion = (): RegionContextType => {
  const ctx = useContext(RegionContext);
  if (!ctx) throw new Error('useRegion must be used within a RegionProvider');
  return ctx;
};
